import { clone } from "../../utils/CoreUtils";
import { cacheByTickCount } from "../Caching";
import { applyTransformations } from "../calculation/Calculation";
import { TransformationTags } from "../calculation/TransformationTags";
import { setFlag } from "../Flags";
import { PlayerContextState } from "../PlayerContext";
import { addContextListener } from "../PlayerContextListeners";
import { Item, ItemOccurrence, ItemStack } from "./Item";
import { getItemById, isInventoryUnlocked } from "./Items";

export function getInventory(state: PlayerContextState): ItemStack[] {
  return state?.inventory || [];
}

function getItemOccurrenceIndex(
  itemOccurrence: ItemOccurrence,
  state: PlayerContextState,
): number {
  const itemOccurrenceParamsJSON = JSON.stringify(itemOccurrence?.params);
  return getInventory(state).findIndex((itemStack) => {
    return (
      itemStack.itemOccurrence.itemId == itemOccurrence?.itemId &&
      itemOccurrenceParamsJSON ==
        JSON.stringify(itemStack.itemOccurrence.params)
    );
  });
}

export function calculateInventoryCap(state: PlayerContextState): number {
  return cacheByTickCount(
    state,
    (s) => "inventoryCap",
    (s) => applyTransformations([TransformationTags.InventorySlot], s, 12),
  );
}

export function addToInventory(
  itemOccurrence: ItemOccurrence,
  amount: number,
  state: PlayerContextState,
): PlayerContextState {
  const previousIndex = getItemOccurrenceIndex(itemOccurrence, state);

  if (previousIndex >= 0) {
    if (!state.inventory) {
      state.inventory = [];
    }
    state.inventory[previousIndex] = {
      itemOccurrence,
      amount: state.inventory[previousIndex].amount + amount,
    };
    return state;
  }

  const inventorySize = getInventory(state).length;
  if (inventorySize >= calculateInventoryCap(state)) {
    return state;
  }

  if (!state.inventory) {
    state.inventory = [];
  }
  state.inventory.push({
    itemOccurrence,
    amount,
  });

  return state;
}

export function removeFromInventory(
  itemOccurrence: ItemOccurrence,
  amount: number,
  state: PlayerContextState,
): PlayerContextState {
  const previousIndex = getItemOccurrenceIndex(itemOccurrence, state);
  if (previousIndex < 0) {
    return state;
  }

  if (!state.inventory) {
    state.inventory = [];
  }
  const newAmount = state.inventory[previousIndex].amount - amount;
  if (newAmount > 0) {
    state.inventory[previousIndex] = {
      itemOccurrence,
      amount: newAmount,
    };
  } else {
    state.inventory.splice(previousIndex, 1);
  }
  return state;
}

export function getAmountOfItem(
  itemOccurrence: ItemOccurrence,
  state: PlayerContextState,
): number {
  const index = getItemOccurrenceIndex(itemOccurrence, state);
  if (index === -1) {
    return 0;
  }
  return getInventory(state)[index].amount;
}

export function getTotalAmountOfItem(
  item: Item,
  state: PlayerContextState,
): number {
  return getInventory(state)
    .filter((itemStack) => itemStack.itemOccurrence.itemId == item.getId())
    .reduce((count, current) => count + current.amount, 0);
}

export function removeFromInventoryWithAnyParams(
  item: Item,
  amount: number,
  state: PlayerContextState,
): PlayerContextState {
  let newState = clone(state);

  while (amount > 0) {
    const inventory = getInventory(newState);
    const firstIndex = inventory.findIndex(
      (itemStack) => itemStack.itemOccurrence.itemId == item.getId(),
    );
    if (firstIndex < 0) {
      return state; // abort
    }
    const itemAmount = inventory[firstIndex].amount;
    newState = removeFromInventory(
      inventory[firstIndex].itemOccurrence,
      Math.min(itemAmount, amount),
      newState,
    );
    amount -= itemAmount;
  }

  return newState;
}

// Place items with those tags in order
const ITEM_TAG_ORDER = ["Consumable", "Hand", "Body", "Accessory"];
// Then if two items have the same tags from the above list, put those who
// have this tag in the back of that sub-category
const ITEM_TAG_LAST_ORDER = ["Material"];

function hasTag(
  itemOccurrence: ItemOccurrence,
  state: PlayerContextState,
  tag: string,
): boolean {
  const item = getItemById(itemOccurrence.itemId);
  return item
    .getTags(state, itemOccurrence.params)
    .some((innerTag) => innerTag.tag === tag);
}

export function sortInventory(state: PlayerContextState): PlayerContextState {
  state.inventory.sort((a, b) => {
    for (let tag of ITEM_TAG_ORDER) {
      const aHasTag = hasTag(a.itemOccurrence, state, tag);
      const bHasTag = hasTag(b.itemOccurrence, state, tag);
      if (aHasTag && !bHasTag) {
        return -1;
      }
      if (bHasTag && !aHasTag) {
        return 1;
      }
    }
    for (let tag of ITEM_TAG_LAST_ORDER) {
      const aHasTag = hasTag(a.itemOccurrence, state, tag);
      const bHasTag = hasTag(b.itemOccurrence, state, tag);
      if (aHasTag && !bHasTag) {
        return 1;
      }
      if (bHasTag && !aHasTag) {
        return -1;
      }
    }
    return 0;
  });
  return state;
}

export function loadInventoryListener() {
  addContextListener("InventoryUnlockerListener", (newState) => {
    if (getInventory(newState).length > 0 && !isInventoryUnlocked(newState)) {
      return setFlag("new_inventory_unlocked");
    }
  });
}
